/**
 * Gemini text embeddings — R-64 section drift.
 * Uses text-embedding-004 via the shared @google/genai client (geminiClient).
 */
import { ai } from '../../services/geminiClient';
import { withGenAIRetry } from '../../services/genaiRetry';

const EMBEDDING_MODEL = 'text-embedding-004';
const DEFAULT_DIMENSIONALITY = 768;

export type EmbedTaskType = 'RETRIEVAL_DOCUMENT' | 'RETRIEVAL_QUERY' | 'SEMANTIC_SIMILARITY';

export interface EmbedTextOptions {
  taskType?: EmbedTaskType;
  outputDimensionality?: number;
}

function extractEmbeddingValues(response: {
  embedding?: { values?: number[] };
  embeddings?: Array<{ values?: number[] }>;
}): number[] {
  if (response.embedding?.values?.length) return response.embedding.values;
  const first = response.embeddings?.[0]?.values;
  return first?.length ? first : [];
}

/** Embed a single text string. Returns empty array on failure (caller treats as no-signal). */
export async function embedText(text: string, opts: EmbedTextOptions = {}): Promise<number[]> {
  const trimmed = text.trim();
  if (!trimmed) return [];

  try {
    // C10 — wrap embedContent in withGenAIRetry so a 429 burst (rate-limit
    // storm) backs off + retries instead of failing immediately. The outer
    // try/catch is retained so an *exhausted* 429 (or any fatal class) still
    // resolves to [] (no-signal) without crashing the caller.
    const response = await withGenAIRetry(
      () =>
        ai.models.embedContent({
          model: EMBEDDING_MODEL,
          contents: trimmed,
          config: {
            taskType: opts.taskType ?? 'RETRIEVAL_DOCUMENT',
            outputDimensionality: opts.outputDimensionality ?? DEFAULT_DIMENSIONALITY,
          },
        }),
      { label: 'embedText', maxAttempts: 4, baseDelayMs: 500, maxBackoffMs: 8000 },
    );
    return extractEmbeddingValues(response as {
      embedding?: { values?: number[] };
      embeddings?: Array<{ values?: number[] }>;
    });
  } catch (err) {
    console.warn('[embedText] embedContent failed:', (err as Error)?.message);
    return [];
  }
}
